import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { urlEliminazione, messaggioEliminazione, avvisaConflitto } from '../../utils/eliminazione';
import Layout from '../../components/Layout';
import stili from '../../components/competenze/stili';
import ConflittoVersione, { eConflitto } from '../../components/ConflittoVersione';
import { usePuoScrivere } from '../../utils/permessi';

// Corrispondenza CCNL + codice livello -> livello leggibile. Il codice del livello (es. 000007) ha significato
// solo insieme al CCNL e non è mai un numero. Se la corrispondenza non c'è, il livello resta vuoto e la riga da verificare.
const ORIGINI = { confermato_da_marco: 'confermato da Marco', inserito_da_utente: 'inserito da utente', appreso_da_file: 'appreso da file' };

export default function LivelliCcnl() {
  const puoScrivere = usePuoScrivere('scadenziario');
  const [livelli, setLivelli] = useState([]);
  const [inModifica, setInModifica] = useState(null);
  const [errore, setErrore] = useState('');

  const carica = useCallback(() => {
    api.get('/scadenze/unilav/livelli').then(r => setLivelli(r.data.livelli)).catch(err => setErrore(err.response?.data?.errore || 'Errore nel caricamento'));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const elimina = async (l) => {
    if (!window.confirm(`Spostare nel Cestino la corrispondenza CCNL ${l.ccnl_codice} · livello ${l.livello_codice}?`)) return;
    try { await api.delete(urlEliminazione(`/scadenze/unilav/livelli/${l.id}`, l.version)); carica(); } catch (err) { setErrore(avvisaConflitto(err, messaggioEliminazione(err))); if (eConflitto(err)) carica(); }
  };

  return (
    <Layout>
      <div style={stili.topBar}>
        <h2 style={{ margin: 0 }}>Livelli CCNL</h2>
        {puoScrivere && <button onClick={() => setInModifica({ voce: null })} style={stili.btn}>+ Nuova corrispondenza</button>}
      </div>
      <p style={{ color: '#555', fontSize: '0.88rem', marginTop: 0 }}>
        Il codice del livello vale solo insieme al suo CCNL e al sistema di codici (Veneto o Friuli: non si mescolano). Il CCNL non compare mai nelle descrizioni delle righe.
      </p>
      {errore && <div style={stili.errore}>{errore}</div>}
      <div style={{ overflowX: 'auto' }}>
      <table style={stili.table}>
        <thead><tr><th style={stili.th}>Sistema</th><th style={stili.th}>CCNL</th><th style={stili.th}>Descrizione CCNL</th><th style={stili.th}>Codice livello</th><th style={stili.th}>Livello</th><th style={stili.th}>Origine</th>{puoScrivere && <th style={stili.th}>Azioni</th>}</tr></thead>
        <tbody>
          {livelli.map(l => (
            <tr key={l.id} style={stili.tr}>
              <td style={stili.td}>{l.regione === 'friuli' ? 'Friuli' : 'Veneto'}</td>
              <td style={stili.td}>{l.ccnl_codice}</td>
              <td style={stili.td}>{l.ccnl_descrizione}</td>
              <td style={stili.td}>{l.livello_codice}</td>
              <td style={stili.td}>{l.livello}</td>
              <td style={stili.td}>{ORIGINI[l.origine] || 'già in tabella'}</td>
              {puoScrivere && (
                <td style={stili.td}>
                  <button onClick={() => setInModifica({ voce: l })} style={stili.btnSmall}>Modifica</button>
                  <button onClick={() => elimina(l)} style={stili.btnSmallDanger}>Elimina</button>
                </td>
              )}
            </tr>
          ))}
          {livelli.length === 0 && <tr><td colSpan={7} style={{ ...stili.td, color: '#999' }}>Nessuna corrispondenza</td></tr>}
        </tbody>
      </table>
      </div>
      {inModifica && <FormLivello voce={inModifica.voce} onChiudi={() => setInModifica(null)} onSalvato={() => { setInModifica(null); carica(); }} />}
    </Layout>
  );
}

function FormLivello({ voce, onChiudi, onSalvato }) {
  const daVoce = v => ({ regione: v?.regione || 'veneto', ccnl_codice: v?.ccnl_codice || '', ccnl_descrizione: v?.ccnl_descrizione || '', livello_codice: v?.livello_codice || '', livello: v?.livello || '', version: v?.version });
  const [form, setForm] = useState(daVoce(voce));
  const [errore, setErrore] = useState('');
  const [conflitto, setConflitto] = useState(null);
  const campo = (nome, etichetta, extra = {}) => (
    <div style={stili.campo}>
      <label style={stili.label}>{etichetta}</label>
      <input value={form[nome]} onChange={e => setForm(f => ({ ...f, [nome]: e.target.value }))} style={stili.input} {...extra} />
    </div>
  );

  const salva = async (version) => {
    setErrore('');
    try {
      if (voce) await api.put(`/scadenze/unilav/livelli/${voce.id}`, { ...form, version });
      else await api.post('/scadenze/unilav/livelli', form);
      onSalvato();
    } catch (err) {
      if (eConflitto(err)) setConflitto(err.response.data);
      else setErrore(err.response?.data?.errore || 'Errore nel salvataggio');
    }
  };

  return (
    <div style={stili.overlay}>
      <div style={{ ...stili.modal, maxWidth: '480px' }}>
        <h3 style={{ marginTop: 0 }}>{voce ? 'Modifica corrispondenza' : 'Nuova corrispondenza'}</h3>
        <form onSubmit={e => { e.preventDefault(); salva(form.version); }}>
          {errore && <div style={stili.errore}>{errore}</div>}
          <div style={stili.campo}>
            <label style={stili.label}>Sistema di codici</label>
            <select value={form.regione} onChange={e => setForm(f => ({ ...f, regione: e.target.value }))} style={stili.input} disabled={Boolean(voce)}>
              <option value="veneto">Veneto</option>
              <option value="friuli">Friuli Venezia Giulia</option>
            </select>
          </div>
          {campo('ccnl_codice', 'Codice CCNL', { required: true, placeholder: 'es. 7330' })}
          {campo('ccnl_descrizione', 'Descrizione CCNL (promemoria)')}
          {campo('livello_codice', 'Codice livello (testo, come nel documento)', { required: true, placeholder: 'es. 000007' })}
          {campo('livello', 'Livello leggibile', { required: true, placeholder: 'es. 5' })}
          <div style={stili.azioni}>
            <button type="button" onClick={onChiudi} style={stili.btnSecondary}>Annulla</button>
            <button type="submit" style={stili.btn}>Salva</button>
          </div>
        </form>
      </div>
      {conflitto && <ConflittoVersione conflitto={conflitto} onRicarica={() => { setForm(daVoce(conflitto.attuale)); setConflitto(null); }} onSovrascrivi={() => salva(conflitto.attuale.version)} />}
    </div>
  );
}
